import { getEquipmentDashboardQueryOptions } from "@packages/api-client/hooks/equipmentController";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Plus, Truck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { DashboardSkeleton } from "@/components/ui/skeleton";
import { useFarmScope } from "@/hooks/useFarmScope";
import { requireReachableRoute } from "@/lib/offline/routeGuard";
import { EquipmentDashboard } from "./-components/EquipmentDashboard";

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/equipment/")({
	// KPI/maintenance dashboard is analytical desk work — online-only by design.
	// The offline field entry points are the fleet list and breakdown report.
	staticData: { offline: "online-only" },
	beforeLoad: () => requireReachableRoute("online-only"),
	loader: async ({ context, params }) => {
		await context.queryClient.ensureQueryData(
			getEquipmentDashboardQueryOptions(params.orgId),
		);
	},
	component: EquipmentDashboardPage,
});

function EquipmentDashboardPage() {
	const { orgId } = Route.useParams();
	const { t } = useTranslation();
	const scope = useFarmScope();

	const { data: dashboardData, isLoading } = useQuery(
		getEquipmentDashboardQueryOptions(orgId, {
			establishmentId:
				scope.kind === "single" ? scope.establishmentId : undefined,
		}),
	);

	if (isLoading) {
		return <DashboardSkeleton />;
	}

	const isEmpty =
		!dashboardData ||
		(dashboardData.kpis.totalActiveAssets.value === 0 &&
			dashboardData.maintenanceDueToday.length === 0);

	return (
		<div className="flex flex-col gap-4">
			{isEmpty || !dashboardData ? (
				<div className="flex flex-col items-center justify-center py-16 gap-4">
					<div className="p-4 rounded-full bg-base-200">
						<Truck className="h-10 w-10 text-base-content/40" />
					</div>
					<div className="text-center">
						<h3 className="text-lg font-semibold">
							{t("equipment.dashboard.emptyTitle")}
						</h3>
						<p className="text-sm text-base-content/60 mt-1 max-w-md">
							{t("equipment.dashboard.emptyDescription")}
						</p>
					</div>
					<Button asChild>
						<Link to="/app/org/$orgId/equipment/fleet/new" params={{ orgId }}>
							<Plus className="h-4 w-4 mr-2" />
							{t("equipment.dashboard.addEquipment")}
						</Link>
					</Button>
				</div>
			) : (
				<EquipmentDashboard dashboard={dashboardData} orgId={orgId} />
			)}
		</div>
	);
}
